import { ErrorHandler, Injectable } from '@angular/core';
import { addAngularError } from '@datadog/browser-rum-angular';

/**
 * Stale-chunk / deploy-skew failures. The app builds with the esbuild
 * `@angular/build:application` builder, which emits hash-named chunks. After a
 * new deploy those filenames change; a browser still holding a cached
 * index.html requests chunks that no longer exist and a dynamic `import()`
 * rejects. The message text varies by browser.
 */
const DYNAMIC_IMPORT_ERROR =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i;

/** sessionStorage key holding the epoch-ms of the last recovery reload. */
const RELOAD_STAMP_KEY = 'chunk-reload-at';

/** Suppress a repeat reload within this window so a persistent failure can't loop. */
const RELOAD_COOLDOWN_MS = 15_000;

/**
 * Global error handler that recovers from stale-chunk module-load failures by
 * reloading the page once (guarded so it can never loop). Replaces
 * `provideDatadogErrorHandler()` in `app.module.ts`, so it forwards every error
 * to Datadog RUM itself via `addAngularError()` to keep RUM error reporting.
 */
@Injectable()
export class GlobalErrorHandler extends ErrorHandler {
  public override handleError(error: unknown): void {
    // Preserve Datadog RUM forwarding — this class replaces
    // provideDatadogErrorHandler(), which did exactly this. RUM must never
    // break the app, so guard the call.
    try {
      addAngularError(error);
    } catch {
      /* never let error reporting throw from the error handler */
    }

    const message =
      error instanceof Error ? error.message : String(error ?? '');
    if (DYNAMIC_IMPORT_ERROR.test(message) && this.shouldReload()) {
      this.reloadPage();
      return;
    }

    // Angular's default handler logs to the console — this avoids a direct
    // console call, which the lint config forbids.
    super.handleError(error);
  }

  /** Hard reload seam — overridable in tests (jsdom locks window.location). */
  protected reloadPage(): void {
    window.location.reload();
  }

  /**
   * Returns true (and records the attempt) at most once per cooldown window.
   * A stale-chunk failure that recurs immediately after a reload means the
   * reload didn't help, so we stop and report instead of looping. A recurrence
   * long after (a fresh deploy skew) is past the cooldown, so we reload again.
   * If storage is unavailable we can't guard against a loop, so we decline.
   */
  private shouldReload(): boolean {
    try {
      const last = Number(
        window.sessionStorage.getItem(RELOAD_STAMP_KEY) ?? '0'
      );
      const now = Date.now();
      if (now - last < RELOAD_COOLDOWN_MS) {
        return false;
      }
      window.sessionStorage.setItem(RELOAD_STAMP_KEY, String(now));
      return true;
    } catch {
      return false;
    }
  }
}
